// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.511Z
// Collection: usernames/$usernameLower
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Username registry keyed by lowercase username for case-insensitive uniqueness. Each doc maps a claimed username to its owner wallet. Deletable by the current owner OR by PROJECT_VAULT_ADDRESS (backend) to release the username back to the pool during creator-profile teardown.
 */

export interface UsernamesRequest {
  username: string;
  owner: AddressType;
  claimedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UsernamesResponse {
  username: string;
  owner: string;
  claimedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Usernames operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUsernames(usernameLower: string, data?: UsernamesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `usernames/${usernameLower}`, document: doc };
}

/**
 * Authenticated user claims a username by writing usernames/<lowercase> with owner=their wallet. Caller must equal @newData.owner. Username length must be 4-15 chars. Document ID MUST be the lowercase form of @newData.username; frontend must enforce this and the character whitelist (letters/numbers/underscores only matching ^[a-zA-Z0-9_]{4,15}$). Uniqueness is enforced by collection key — a different owner cannot overwrite an existing doc because update requires @user.address == @data.owner. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUsernames(usernameLower: string, data?: UsernamesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`usernames/${usernameLower}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Usernames: ${formatError(error)}`);
    return false;
  }
}

export type UsernamesRequestUpdate = Partial<UsernamesRequest>;

/**
 * Build a Usernames update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateUsernames(usernameLower: string, data: UsernamesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `usernames/${usernameLower}`, document: doc };
}

/**
 * Only the current owner can update their own username doc (e.g., correct display casing). Owner field is readonly. Length validation re-applied (4-15 chars). (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateUsernames(usernameLower: string, data: UsernamesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`usernames/${usernameLower}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Usernames: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read username registry to check ownership or look up wallets.
   (Get Single Item)
 */
export async function getUsernames(usernameLower: string): Promise<UsernamesResponse | null> {
  try {
    const result = await get(`usernames/${usernameLower}`);
    return result as UsernamesResponse | null;
  } catch (error) {
    console.error(`Error getting Usernames: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Usernames document. (
  Read Operation Details: Anyone can read username registry to check ownership or look up wallets.
  )
 */
export function subscribeUsernames(callback: (data: UsernamesResponse | null) => void, usernameLower: string): Promise<() => Promise<void>> {
  return subscribe(`usernames/${usernameLower}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UsernamesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Usernames at path '${`usernames/${usernameLower}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Usernames items from collection usernames
 
  Read Operation Details: Anyone can read username registry to check ownership or look up wallets.
  
 */
export async function getManyUsernames(filter: string = ""): Promise<UsernamesResponse[]> {
  try {
    const results = await get("usernames", { prompt: filter });
    return Array.isArray(results) ? results as UsernamesResponse[] : [];
  } catch (error) {
    console.error(`Error getting Usernames collection at path 'usernames': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Usernames collection at usernames
 
  Read Operation Details: Anyone can read username registry to check ownership or look up wallets.
  
 */
export function subscribeManyUsernames(callback: (data: UsernamesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("usernames", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsernamesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Usernames collection at path '${"usernames"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Usernames items from collection usernames
 
  Read Operation Details: Anyone can read username registry to check ownership or look up wallets.
  
 */
export async function getAllUsernames(filter: string = ""): Promise<UsernamesResponse[]> {
  try {
    const results = await get("usernames", { prompt: filter });
    return Array.isArray(results) ? results as UsernamesResponse[] : [];
  } catch (error) {
    console.error(`Error getting Usernames collection at path 'usernames': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Usernames collection at usernames
 
  Read Operation Details: Anyone can read username registry to check ownership or look up wallets.
  
 */
export function subscribeAllUsernames(callback: (data: UsernamesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("usernames", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsernamesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Usernames collection at path '${"usernames"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Usernames items in collection usernames.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countUsernames(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("usernames", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Usernames at path 'usernames': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Usernames items in collection usernames.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateUsernames(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("usernames", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Usernames at path 'usernames': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Allowed for the current owner (used when changing usernames to free up the old lowercase key) OR for PROJECT_VAULT_ADDRESS (backend creator-profile teardown via DELETE /api/profile — releases the username back to the pool atomically with userProfiles/userAvatars deletion and wallpaper soft-delete).
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteUsernames(usernameLower: string): Promise<boolean> {
  try {
    const succeeded = await set(`usernames/${usernameLower}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Usernames: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Usernames for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteUsernames(usernameLower: string): DocumentOperation {
  return { path: `usernames/${usernameLower}`, document: null };
}

